import React from 'react';
import { motion } from 'framer-motion';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useLocale, useTranslations } from 'next-intl';

interface NewsCardProps {
    title: string;
    description: string;
    date: string | Date;
    imageUrl: string;
    slug: string;
    className?: string;
    compact?: boolean; // For landing page dense view
    onDelete?: (e: React.MouseEvent) => void;
    deleteStatus?: 'idle' | 'confirming' | 'deleting';
}

export function NewsCard({
    title,
    description,
    date,
    imageUrl,
    slug,
    className,
    compact = false,
    onDelete,
    deleteStatus = 'idle'
}: NewsCardProps) {
    const t = useTranslations('landing.newsSection');
    const locale = useLocale();
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Format date for the badge
    const month = format(dateObj, 'MMM'); // e.g., JAN
    const year = format(dateObj, 'yyyy'); // e.g., 2024

    return (
        <div className={cn("relative block h-full group", className)}>
            <Link
                href={`/news/${slug}`}
                className="block h-full break-inside-avoid"
            >
                <motion.div
                    className={cn(
                        "bg-white overflow-hidden transition-colors",
                        compact
                            ? "flex flex-row h-24 hover:bg-slate-50"
                            : "border border-slate-200 hover:border-slate-300 h-full flex flex-col"
                    )}
                    whileHover={!compact ? { y: -2 } : {}}
                    transition={{ duration: 0.2 }}
                >
                    <div className={cn(
                        "relative overflow-hidden",
                        compact ? "w-24 min-w-[6rem] h-full" : "h-[180px] w-full"
                    )}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={imageUrl}
                            alt={title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />

                        {/* Date Badge */}
                        {!compact && (
                            <div className="absolute top-0 left-0 bg-white text-black p-2 flex flex-col items-center justify-center min-w-[3.5rem] shadow-sm z-10">
                                <span className="text-xs font-bold uppercase tracking-wider">{month}</span>
                                <span className="text-sm font-light">{year}</span>
                            </div>
                        )}

                        {/* Delete button - bottom-right of image */}
                        {onDelete && !compact && (
                            <div className="absolute bottom-0 right-0 flex items-end pb-2 pr-2 z-20">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDelete(e);
                                    }}
                                    className={cn(
                                        "p-1.5 rounded transition-colors cursor-pointer backdrop-blur-sm",
                                        deleteStatus === 'idle'
                                            ? "text-white/80 hover:text-red-400 bg-black/40 hover:bg-black/60 opacity-0 group-hover:opacity-100"
                                            : deleteStatus === 'confirming'
                                                ? "text-red-400 bg-black/60"
                                                : "text-red-400 bg-black/60"
                                    )}
                                    title={deleteStatus === 'confirming' ? t('confirmDelete') : t('delete')}
                                >
                                    {deleteStatus === 'deleting' ? (
                                        <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                    ) : deleteStatus === 'confirming' ? (
                                        <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={cn(
                        "flex flex-col justify-center", // Center vertically for horizontal layout
                        compact ? "p-3 w-full" : "p-5"
                    )}>
                        {compact && (
                            <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mb-1">
                                {month} {year}
                            </div>
                        )}
                        <h3 className={cn(
                            "font-bold text-gray-900 group-hover:underline decoration-1 underline-offset-4",
                            compact ? "text-sm leading-tight line-clamp-2 mb-0" : "text-lg leading-snug mb-2"
                        )}>
                            {title}
                        </h3>
                        {!compact && (
                            <p className="text-gray-500 line-clamp-3 text-sm">
                                {description}
                            </p>
                        )}
                    </div>
                </motion.div>
            </Link>

        </div>
    );
}
